// Morale, suppression, and the two faction hooks (GDD §3.1/§3.2/§4.3).
//
// Suppression (0..1): raised by incoming fire, decays out of fire; high suppression pins
// models (movement penalty) then puts them prone. Morale: drained by fire/casualties/fear,
// regens out of combat and faster near a hero/HQ. At 0 a squad BREAKS — Legion (and most)
// flee toward safety and take reduced damage; the Kindled instead FRENZY (uncontrollable
// charge). Legion Doctrine: squads that hold cover accrue discipline (accuracy + morale
// resist). Kindled Fervor: a shared meter fed by kills/charges granting speed + melee.

import { dist } from "../engine/vec";
import type { Squad } from "./types";
import { TUNE, World } from "./world";

export function stepMorale(world: World, dt: number): void {
  // Kindled Fervor: army-shared meter → tiers; slow decay.
  const k = world.armies.kindled;
  k.fervor = Math.max(0, k.fervor - TUNE.fervorDecay * dt);
  k.fervorTier = k.fervor >= 0.66 ? 2 : k.fervor >= 0.33 ? 1 : 0;

  for (const sq of world.squads) {
    if (sq.dead) continue;
    // cooldowns
    for (const id in sq.abilityCd) if (sq.abilityCd[id] > 0) sq.abilityCd[id] = Math.max(0, sq.abilityCd[id] - dt);

    sq.timeSinceHit += dt;
    const underFire = sq.timeSinceHit < 1.5;

    // suppression decay
    if (!underFire) sq.suppression = Math.max(0, sq.suppression - TUNE.suppressionDecay * dt);

    // near-support bonuses (hero aura / friendly HQ)
    const nearSupport = isNearSupport(world, sq);

    // Legion Doctrine — discipline while holding cover, stationary.
    if (sq.faction === "legion") {
      const holding = sq.path.length === 0 && !sq.broken;
      const inCover = sq.models.some((m) => m.alive && (m.coverTarget !== null || m.posture === "incover"));
      if (holding && inCover) sq.discipline = Math.min(TUNE.disciplineMax, sq.discipline + TUNE.disciplineGain * dt);
      else sq.discipline = Math.max(0, sq.discipline - TUNE.disciplineDecay * dt);
    }

    // morale regen
    if (!underFire) {
      let regen = TUNE.moraleRegen;
      if (nearSupport) regen *= 2.2;
      // discipline & aura resist help morale recover
      regen *= 1 + sq.discipline * 0.5 + sq.auraResist;
      sq.morale = Math.min(sq.maxMorale, sq.morale + regen * dt);
    } else {
      sq.morale = Math.min(sq.maxMorale, sq.morale + TUNE.moraleRegen * TUNE.moraleRegenCombatFactor * dt);
    }

    // retreat stance costs morale while moving (GDD §7.1)
    if (sq.stance === "retreat" && sq.path.length > 0) sq.morale = Math.max(0, sq.morale - 3 * dt);

    // BREAK
    if (!sq.broken && sq.morale <= 0) breakSquad(world, sq);

    // RALLY
    if (sq.broken) {
      const recovered = sq.morale >= sq.maxMorale * 0.4;
      const safe = !underFire && (sq.timeSinceHit > TUNE.rallyTime || nearSupport);
      if ((recovered && safe) || (nearSupport && sq.morale >= sq.maxMorale * 0.25)) rallySquad(sq);
    }
  }
}

function breakSquad(world: World, sq: Squad): void {
  sq.broken = true;
  world.stats.moraleBreaks++;
  if (sq.faction === "kindled") {
    sq.frenzy = true; // Kindled frenzy instead of flee
    world.stats.frenzies++;
    world.armies.kindled.fervor = Math.min(1, world.armies.kindled.fervor + 0.05);
    world.addEffect({ kind: "text", a: { ...sq.center }, t: 0, ttl: 1.4, text: "FRENZY!", faction: "kindled" });
  } else {
    world.addEffect({ kind: "text", a: { ...sq.center }, t: 0, ttl: 1.4, text: "BREAKING!", faction: sq.faction });
    // fleeing squads drop attack orders
    sq.order = { type: "hold", pos: { ...sq.center }, facing: sq.facing, targetSquad: -1, targetPoint: -1 };
  }
  sq.morale = 1;
}

function rallySquad(sq: Squad): void {
  sq.broken = false;
  sq.frenzy = false;
  sq.morale = Math.max(sq.morale, sq.maxMorale * 0.45);
  sq.order = { type: "hold", pos: { ...sq.center }, facing: sq.facing, targetSquad: -1, targetPoint: -1 };
}

function isNearSupport(world: World, sq: Squad): boolean {
  const hero = world.armies[sq.faction].hero;
  if (hero && !hero.dead && hero !== sq && dist(hero.center, sq.center) < (hero.def.aura?.radius ?? 240)) return true;
  for (const s of world.structures) {
    if (s.faction !== sq.faction || s.hp <= 0) continue;
    if ((s.kind === "hq" || s.kind === "barracks") && dist(s.pos, sq.center) < 300) return true;
  }
  return false;
}
